"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  LEVEL_COLOR,
  LEVEL_DOT_CLASS,
  LEVEL_LABEL,
  LEVEL_ORDER,
} from "./levels";

const R = 42;
const C = 2 * Math.PI * R;
const STROKE = 22;
const R_IN = R - STROKE / 2;
const R_OUT = R + STROKE / 2;
const OVERLAP = 1; // minimalne zachodzenie kolorów (żeby pod separatorem nie było szwu)
const SEP_W = 2.5; // stała szerokość przerwy (białej linii) — równe boki niezależnie od promienia
// Minimalna szerokość wycinka (frakcja obwodu) — gwarantuje klikalność małych kolorów.
// Kosztem proporcji: drobne wartości dostają tę szerokość, reszta oddaje proporcjonalnie.
const MIN_SEGMENT_FRACTION = 1 / 20;

/**
 * Podnosi zbyt małe frakcje do `min`, a nadwyżkę odejmuje proporcjonalnie od większych.
 * Zachowuje sumę = 1 (dla n wartości i n*min < 1 — u nas n<=4, min=1/20).
 */
function clampMinFractions(raw: number[], min: number): number[] {
  const deficit = raw.reduce((a, f) => a + Math.max(0, min - f), 0);
  const donorPool = raw.reduce((a, f) => a + Math.max(0, f - min), 0);
  if (deficit === 0 || donorPool === 0) return raw;
  return raw.map((f) =>
    f < min ? min : f - (f - min) * (deficit / donorPool),
  );
}

// Punkty białej linii-separatora w promieniu (frakcja obwodu od startu ścieżki = 3 godzina).
const separatorLine = (frac: number) => {
  const a = 2 * Math.PI * frac;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return {
    x1: 60 + cos * (R_IN - 1),
    y1: 60 + sin * (R_IN - 1),
    x2: 60 + cos * (R_OUT + 1),
    y2: 60 + sin * (R_OUT + 1),
  };
};

export const LevelChart = ({
  levels,
  total,
  activeLevels,
  onLevelClick,
}: {
  levels: ExamSrProgressLevelVM[];
  total: number;
  activeLevels: ExamSrProgressLevel[];
  onLevelClick: (level: ExamSrProgressLevel) => void;
}) => {
  const [hovered, setHovered] = useState<ExamSrProgressLevel | null>(null);
  const hasSelection = activeLevels.length > 0;

  const countOf = (level: ExamSrProgressLevel) =>
    levels.find((l) => l.level === level)?.count ?? 0;

  const safeTotal = Math.max(1, total);

  // Tylko obecne kolory; minimalna frakcja gwarantuje klikalność (kosztem proporcji).
  const present = LEVEL_ORDER.filter((level) => countOf(level) > 0);
  const adjusted = clampMinFractions(
    present.map((level) => countOf(level) / safeTotal),
    MIN_SEGMENT_FRACTION,
  );

  let offset = 0;
  const segments = present.map((level, i) => {
    const len = adjusted[i] * C;
    const seg = { level, count: countOf(level), len, offset };
    offset += len;
    return seg;
  });

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg viewBox="0 0 120 120" className="h-[220px] w-[220px] -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="#f1f5f9"
            strokeWidth={STROKE}
          />
          {segments.map((s) => {
            const isActive = activeLevels.includes(s.level);
            const dimmed =
              (hasSelection && !isActive) ||
              (hovered !== null && hovered !== s.level);

            return (
              <circle
                key={s.level}
                cx="60"
                cy="60"
                r={R}
                fill="none"
                stroke={LEVEL_COLOR[s.level]}
                strokeWidth={STROKE}
                strokeDasharray={`${s.len + OVERLAP} ${C - s.len - OVERLAP}`}
                strokeDashoffset={-s.offset}
                onMouseEnter={() => setHovered(s.level)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onLevelClick(s.level)}
                className={cn(
                  "cursor-pointer transition-opacity",
                  dimmed && "opacity-30",
                )}
              />
            );
          })}

          {/* Białe separatory o stałej szerokości — tworzą równe przerwy między kolorami. */}
          {segments.length > 1 &&
            segments.map((s) => {
              const p = separatorLine((s.offset + s.len) / C);
              return (
                <line
                  key={`sep-${s.level}`}
                  x1={p.x1}
                  y1={p.y1}
                  x2={p.x2}
                  y2={p.y2}
                  stroke="#ffffff"
                  strokeWidth={SEP_W}
                  className="pointer-events-none"
                />
              );
            })}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-slate-900">{total}</span>
          <span className="text-sm text-slate-400">pytań</span>
        </div>

        {hovered !== null && (
          <div className="pointer-events-none absolute top-1/2 left-[60%] z-10 flex -translate-y-1/2 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs whitespace-nowrap shadow-md">
            <span
              className={cn("size-2 rounded-full", LEVEL_DOT_CLASS[hovered])}
            />
            <span className="text-slate-600">{LEVEL_LABEL[hovered]}</span>
            <span className="font-semibold text-slate-900">
              {countOf(hovered)}
            </span>
          </div>
        )}
      </div>

      <p className="mt-5 text-center text-sm text-slate-500">
        Dane bierzemy tylko z modułu{" "}
        <span className="font-semibold text-slate-700">spaced repetition</span>.
      </p>

      <div className="mt-4 flex w-full flex-col gap-2">
        {LEVEL_ORDER.map((level) => {
          const count = countOf(level);
          const percent = Math.round((count / safeTotal) * 100);
          return (
            <button
              key={level}
              type="button"
              onClick={() => onLevelClick(level)}
              className={cn(
                "flex cursor-pointer items-center gap-2 text-sm",
                hasSelection && !activeLevels.includes(level) && "opacity-50",
              )}
            >
              <span
                className={cn("size-3 rounded-sm", LEVEL_DOT_CLASS[level])}
              />
              <span className="text-slate-600">{LEVEL_LABEL[level]}</span>
              <span className="ml-auto flex items-center gap-2 tabular-nums">
                <span className="font-semibold text-slate-900">{count}</span>
                <span className="text-slate-400">{percent}%</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
